import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Search, Download } from "lucide-react";

function exportCsv(rows) {
  const cols = ["employee_id", "name", "department", "designation", "additional_charge", "type", "status", "doj"];
  const data = rows.map((e) => [
    e.employee_id, e.identity?.name, e.department_name, e.designation_title, e.employment?.additional_charge,
    e.employment?.employee_type, e.employment?.employment_status, e.employment?.doj,
  ]);
  const csv = [cols.join(","), ...data.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = "employees.csv"; a.click();
}

export default function Employees() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dept, setDept] = useState("all");
  const navigate = useNavigate();

  const { data: depts } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/departments")).data });
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["employees", search, status, dept],
    queryFn: async () => {
      const p = {};
      if (search) p.search = search;
      if (status !== "all") p.status = status;
      if (dept !== "all") p.department_id = dept;
      return (await api.get("/employees", { params: p })).data;
    },
  });

  return (
    <div>
      <PageHeader overline="Personnel" title="Employee Master" icon={Users}
        subtitle="The single source of truth for each employee's full lifecycle."
        actions={
          <>
            <Button variant="outline" onClick={() => exportCsv(data || [])} disabled={!data?.length} data-testid="export-employees"><Download className="h-4 w-4 mr-1" /> Export</Button>
            <Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => navigate("/employees/new")} data-testid="add-employee-btn"><Plus className="h-4 w-4 mr-1" /> Add Employee</Button>
          </>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by name or employee ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white focus-visible:ring-teal" data-testid="employee-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Probation">Probation</SelectItem>
            <SelectItem value="Notice">Notice</SelectItem>
            <SelectItem value="Left">Left</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="filter-dept"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Departments</SelectItem>
            {(depts || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : isError ? (
        <ErrorState message="Couldn't load the employee list." onRetry={refetch} />
      ) : (data || []).length === 0 ? (
        <EmptyState icon={Users} title="No employees found" hint="Add a new hire by entering their details in the Employee Master form."
          action={<Button className="bg-navy" onClick={() => navigate("/employees/new")}><Plus className="h-4 w-4 mr-1" /> Add Employee</Button>} />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Emp ID</TableHead>
                <TableHead className="overline text-slate-500">Name</TableHead>
                <TableHead className="overline text-slate-500">Department</TableHead>
                <TableHead className="overline text-slate-500">Designation</TableHead>
                <TableHead className="overline text-slate-500">Type</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((e) => (
                <TableRow key={e.id} className="table-row-i cursor-pointer" onClick={() => navigate(`/employees/${e.id}`)} data-testid={`employee-row-${e.employee_id}`}>
                  <TableCell className="font-mono-i text-sm text-teal">{e.employee_id}</TableCell>
                  <TableCell className="font-medium text-navy">{e.identity?.name}</TableCell>
                  <TableCell className="text-slate-600">{e.department_name || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.designation_title || "—"}{e.employment?.additional_charge && <span className="block text-[11px] text-teal">+ {e.employment.additional_charge}</span>}</TableCell>
                  <TableCell className="text-slate-600">{e.employment?.employee_type || "—"}</TableCell>
                  <TableCell><StatusBadge status={e.employment?.employment_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
